/**
 * @fileoverview ClubOS Sidenav — navigation sidebar with RBAC-aware route filtering
 * Routes with a `permission` or `anyOf` field are only shown to users with that access.
 */

import { useEffect, useState, useCallback } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import { useSoftUIController, setMiniSidenav } from "context";
import { useAuth } from "context/AuthContext";

function Sidenav({ color = "info", brand = "", brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  const { pathname } = location;

  const { currentUser, hasPermission, hasAnyPermission } = useAuth();

  // Track collapsed sections (all expanded by default)
  const [collapsedSections, setCollapsedSections] = useState(() => {
    const stored = localStorage.getItem("clubos_collapsed_sections");
    return stored ? JSON.parse(stored) : {};
  });

  // Get enabled sections from localStorage — re-read on every navigation so
  // changes saved in Settings are reflected immediately without a page reload.
  const readEnabledSections = useCallback(() => {
    try {
      const stored = localStorage.getItem("clubos_enabled_sections");
      return stored ? JSON.parse(stored) : {
        dashboard: true, finance: true, squad: true, coaching: true, scouting: true, academy: true,
      };
    } catch {
      return { dashboard: true, finance: true, squad: true, coaching: true, scouting: true, academy: true };
    }
  }, []);

  const [enabledSections, setEnabledSections] = useState(readEnabledSections);

  useEffect(() => {
    setEnabledSections(readEnabledSections());
  }, [location, readEnabledSections]);

  // Map section titles to section IDs
  const sectionMap = {
    "Dashboards": "dashboard",
    "Finance": "finance",
    "Squad": "squad",
    "Coaching": "coaching",
    "Scouting": "scouting",
    "Youth Academy": "academy",
  };

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Toggle section collapse state and persist to localStorage
  const toggleSectionCollapse = (sectionTitle) => {
    const updated = { ...collapsedSections, [sectionTitle]: !collapsedSections[sectionTitle] };
    setCollapsedSections(updated);
    localStorage.setItem("clubos_collapsed_sections", JSON.stringify(updated));
  };

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }
    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  /**
   * Determine if a route should be visible in the sidenav based on the user's permissions.
   * Routes without a permission field are visible to all authenticated users.
   */
  function isRouteVisible(route) {
    if (route.anyOf) return hasAnyPermission(route.anyOf);
    if (route.permission) return hasPermission(route.permission);
    return true;
  }

  /**
   * Check if a section is enabled based on admin settings
   */
  function isSectionEnabled(sectionTitle) {
    const sectionId = sectionMap[sectionTitle];
    if (!sectionId) return true; // Sections without mapping are always visible
    return enabledSections[sectionId] !== false; // Default to true if not set
  }

  /**
   * Build visible routes with section awareness.
   * Returns routes grouped with their section titles and collapse state.
   */
  function buildVisibleRoutes() {
    const result = [];
    let i = 0;

    while (i < routes.length) {
      const route = routes[i];

      if (route.type === "title") {
        // Check if section is enabled
        if (!isSectionEnabled(route.title)) {
          // Skip this entire section
          let j = i + 1;
          while (j < routes.length && routes[j].type !== "title" && routes[j].type !== "divider") {
            j++;
          }
          i = j;
          continue;
        }

        // Look ahead: collect all collapse items in this section
        let j = i + 1;
        while (j < routes.length && routes[j].type !== "title" && routes[j].type !== "divider") {
          j++;
        }
        const sectionItems = routes.slice(i + 1, j).filter((r) => r.type === "collapse");
        const anyVisible = sectionItems.some((r) => isRouteVisible(r));
        if (anyVisible) {
          result.push({ ...route, sectionTitle: route.title });
        }
        i++;
        continue;
      }

      if (route.type === "divider") {
        result.push(route);
        i++;
        continue;
      }

      if (route.type === "collapse") {
        if (isRouteVisible(route)) result.push(route);
        i++;
        continue;
      }

      i++;
    }

    return result;
  }

  const visibleRoutes = buildVisibleRoutes();

  // Render routes with section awareness and collapse state
  const renderRoutes = visibleRoutes.map(({ type, name, icon, title, noCollapse, key, route, href, sectionTitle }, idx) => {
    if (type === "collapse") {
      // Find the section this item belongs to by looking backward
      let parentSection = null;
      for (let i = idx - 1; i >= 0; i--) {
        if (visibleRoutes[i].type === "title") {
          parentSection = visibleRoutes[i].sectionTitle;
          break;
        }
      }

      // Don't render if parent section is collapsed
      if (parentSection && collapsedSections[parentSection]) {
        return null;
      }

      return href ? (
        <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none" }}>
          <SidenavCollapse color={color} name={name} icon={icon}
            active={key === pathname.split("/").slice(1)[0]} noCollapse={noCollapse} />
        </Link>
      ) : (
        <NavLink to={route} key={key}>
          <SidenavCollapse color={color} key={key} name={name} icon={icon}
            active={pathname === route} noCollapse={noCollapse} />
        </NavLink>
      );
    }

    if (type === "title") {
      const isCollapsed = collapsedSections[title] || false;
      return (
        <SoftBox key={key} display="flex" alignItems="center" justifyContent="space-between" px={2} mt={2} mb={1}>
          <SoftTypography display="block" variant="caption" fontWeight="bold"
            textTransform="uppercase" opacity={0.6} sx={{ flex: 1 }}>
            {title}
          </SoftTypography>
          <Icon
            onClick={() => toggleSectionCollapse(title)}
            sx={{
              fontSize: "1rem",
              cursor: "pointer",
              opacity: 0.6,
              transition: "transform 0.2s ease-in-out",
              transform: isCollapsed ? "rotate(0deg)" : "rotate(-90deg)",
              "&:hover": { opacity: 1 }
            }}
          >
            expand_more
          </Icon>
        </SoftBox>
      );
    }

    if (type === "divider") {
      return <Divider key={key} />;
    }

    return null;
  }).filter(Boolean);

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      {/* Brand / logo */}
      <SoftBox pt={3} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <SoftBox component="img" src={brand} alt="ClubOS Logo" width="2rem" />}
          <SoftBox width={!brandName && "100%"} sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
            <SoftTypography component="h6" variant="button" fontWeight="medium">
              {brandName}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <Divider />

      {/* RBAC-filtered navigation links */}
      <List>{renderRoutes}</List>

      {/* Signed-in user identity at the bottom */}
      {currentUser && (
        <SoftBox p={2} mt="auto">
          <Divider sx={{ my: 1 }} />
          <SoftBox display="flex" alignItems="center" gap={1.5} px={0.5}>
            <SoftAvatar
              size="sm"
              sx={{ bgcolor: "info.main", fontSize: "0.75rem", width: 32, height: 32, flexShrink: 0 }}
            >
              {currentUser.avatar}
            </SoftAvatar>
            <SoftBox overflow="hidden">
              <SoftTypography
                variant="button"
                fontWeight="medium"
                display="block"
                sx={{ lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {currentUser.name}
              </SoftTypography>
              <SoftTypography
                variant="caption"
                color="secondary"
                display="block"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {currentUser.role}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
      )}
    </SidenavRoot>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
